import React, { useMemo } from "react";

import {
  FlamebearerCall,
  FlamebearerNode
} from "@platformsh/observability-graph/ContinuousProfiling";

import useConprofContext from "src/organization/pages/project/environment/observabilitySandbox/experiments/Conprof/hooks/useConprofContext";
import { Range } from "src/organization/pages/project/environment/observabilitySandbox/experiments/Conprof/types";

import FlamebearerGraphNode from "./FlamebearerGraphNode";
import FlamebearerGraphSkeleton from "./FlamebearerGraphSkeleton";
import * as S from "./styles";

type Props = {
  selectedNode: FlamebearerNode | null;
  onNodeSelected: (node: FlamebearerNode | null) => void;
  highlightedCall: FlamebearerCall | null;
  onCallHighlighted: (call: FlamebearerCall | null) => void;
};

const FlamebearerGraph = ({
  selectedNode,
  onNodeSelected,
  highlightedCall,
  onCallHighlighted
}: Props) => {
  const { data } = useConprofContext() ?? {};
  const profileType = data?.profileType;
  const flamebearerData = data?.flamebearerData;

  const { numberOfLines, nodes: flatNodes } = useMemo((): {
    numberOfLines: number;
    nodes: FlamebearerNode[];
  } => {
    if (!flamebearerData) {
      return {
        numberOfLines: 0,
        nodes: []
      };
    }

    const traverse = (
      node: FlamebearerNode
    ): {
      numberOfLines: number;
      nodes: FlamebearerNode[];
    } => {
      if (
        selectedNode &&
        (selectedNode.start > node.end || selectedNode.end < node.start)
      ) {
        return {
          numberOfLines: 0,
          nodes: []
        };
      }

      let numberOfLines: number = node.level;
      const nodes: FlamebearerNode[] = [node];

      for (const child of node.children) {
        const { numberOfLines: childDepth, nodes: childNodes } =
          traverse(child);
        nodes.push(...childNodes);
        if (childDepth > numberOfLines) {
          numberOfLines = childDepth;
        }
      }

      return { numberOfLines, nodes };
    };

    return traverse(flamebearerData.flamebearer.rootNode);
  }, [flamebearerData, selectedNode]);

  const nodes = useMemo((): FlamebearerNode[] => {
    if (!selectedNode) {
      return flatNodes;
    }

    return flatNodes.filter(
      (node: FlamebearerNode) =>
        selectedNode.start <= node.end && selectedNode.end >= node.start
    );
  }, [flatNodes, selectedNode]);

  const topNode = selectedNode ?? flamebearerData?.flamebearer.rootNode;
  const range: Range = useMemo(
    () =>
      topNode ? { from: topNode.start, to: topNode.end } : { from: 0, to: 0 },
    [topNode]
  );

  if (!profileType) {
    return null;
  }
  if (!flamebearerData) {
    return <FlamebearerGraphSkeleton />;
  }

  return (
    <S.Container numberOfLines={numberOfLines}>
      {nodes.map((node: FlamebearerNode) => (
        <FlamebearerGraphNode
          key={`${node.callstack.join(":")}:${node.call.id}`}
          truncated={!!(selectedNode && selectedNode.level > node.level)}
          onNodeSelected={onNodeSelected}
          highlighted={
            !!(highlightedCall && highlightedCall.raw === node.call.raw)
          }
          onCallHighlighted={onCallHighlighted}
          node={node}
          profileType={profileType}
          range={range}
        />
      ))}
    </S.Container>
  );
};

export default FlamebearerGraph;
